import React, { useMemo } from 'react';

import EditableList, { EditableListProps } from './EditableList';

export function EditableListWithPlaceholder<T, U>({
  values,
  getNewValue,
  getId,
  onItemChange,
  onAddNewItem,
  onRemoveItem,
  onReorder,
  renderField,
  ...props
}: EditableListProps<T, U>) {
  type Value = T;
  const [usePlaceholder, setUsePlaceholder] = React.useState(() => values.length === 0);

  const { resolved, placeholderValue } = useMemo(() => {
    const valuesWithoutPlaceholder = values;

    if (!usePlaceholder) {
      return { resolved: valuesWithoutPlaceholder, placeholderValue: undefined };
    }

    const placeholderValue = getNewValue();
    return {
      resolved: [...valuesWithoutPlaceholder, placeholderValue],
      placeholderValue,
    };
  }, [values, usePlaceholder, getNewValue]);

  const filterOutPlaceholder = React.useCallback(
    (values: Value[]) =>
      values.filter((v) =>
        placeholderValue ? getId(v) !== getId(placeholderValue) : true
      ),
    [getId, placeholderValue]
  );

  const onItemChangeWithPlaceholder = React.useCallback(
    async (v: Value, newIndex: number, delta: U, allValues: Value[]) => {
      if (placeholderValue && getId(v) === getId(placeholderValue)) {
        if (onAddNewItem) {
          await onAddNewItem(v, allValues);
          setUsePlaceholder(false);
        }
      } else {
        if (onItemChange) {
          await onItemChange(v, newIndex, delta, filterOutPlaceholder(allValues));
        }
      }
    },
    [placeholderValue, getId, onAddNewItem, onItemChange, filterOutPlaceholder]
  );

  const onAddNewItemWithPlaceholder = React.useCallback(
    async (v: Value, allValues: Value[]) => {
      if (!usePlaceholder) {
        setUsePlaceholder(true);
      } else {
        if (onAddNewItem) {
          await onAddNewItem(v, filterOutPlaceholder(allValues));
        }
      }
    },
    [usePlaceholder, onAddNewItem, filterOutPlaceholder]
  );

  const onRemoveItemWithPlaceholder = React.useCallback(
    async (v: Value, removeIndex: number, allValues: Value[]) => {
      if (placeholderValue && getId(v) === getId(placeholderValue)) {
        setUsePlaceholder(false);
      } else {
        if (onRemoveItem) {
          await onRemoveItem(v, removeIndex, filterOutPlaceholder(allValues));
        }
      }

      if (allValues.length === 0) {
        setUsePlaceholder(true);
      }
    },
    [placeholderValue, getId, onRemoveItem, filterOutPlaceholder]
  );

  const onReorderWithPlaceholder = React.useCallback(
    async (v: Value, currentIndex: number, newIndex: number, allValues: Value[]) => {
      let valuesMaybeWithPlaceholder;

      // if we're trying to move somewhere, add it
      if (placeholderValue && getId(v) === getId(placeholderValue)) {
        valuesMaybeWithPlaceholder = allValues;

        if (onAddNewItem) {
          await onAddNewItem(v, valuesMaybeWithPlaceholder);
        }

        setUsePlaceholder(false);
      } else if (placeholderValue && newIndex === allValues.length - 1) {
        // are we trying to move into the placeholder's location? add it too
        valuesMaybeWithPlaceholder = allValues;

        if (onAddNewItem) {
          await onAddNewItem(placeholderValue, valuesMaybeWithPlaceholder);
        }

        setUsePlaceholder(false);
      } else {
        // didn't include placeholder, ignore it from all values so we don't add it
        valuesMaybeWithPlaceholder = filterOutPlaceholder(allValues);
      }

      if (onReorder) {
        await onReorder(v, currentIndex, newIndex, valuesMaybeWithPlaceholder);
      }
    },
    [placeholderValue, getId, onReorder, onAddNewItem, filterOutPlaceholder]
  );

  return (
    <EditableList<Value, U>
      getId={getId}
      values={resolved}
      onItemChange={onItemChangeWithPlaceholder}
      onAddNewItem={onAddNewItemWithPlaceholder}
      onRemoveItem={onRemoveItemWithPlaceholder}
      onReorder={onReorderWithPlaceholder}
      getNewValue={getNewValue}
      renderField={renderField}
      {...props}
    />
  );
}
